import { useContext } from 'react';
import { WP_DirUser, WP_PaginationResult } from '../model/models';
import { useLogger } from '../services/LogService';
import { ModelsService } from '../services/ModelsService';
import { WP_FetchPage } from '../services/WPService';
import { AppContext } from '../state/AppContextProvider';
import { usePagination, useWP } from './WPHooks';

export type RefreshDirUserFunc = (userId: number) => void;

export interface UsersHookReturn {
  reset: () => void;
  isReset: boolean;
  isLoading: boolean;
  hasMoreUsers: boolean;
  fetchUsers: () => Promise<WP_PaginationResult<WP_DirUser>>;
  page: number;
  totalPage: number;
  users: WP_DirUser[];
  refreshUser: RefreshDirUserFunc;
}

export const useUsers: (per_page: number) => UsersHookReturn = (per_page) => {
  const { wp } = useWP();

  const logger = useLogger('useUsers');

  const { state } = useContext(AppContext);

  // don't display users without name and photo
  const userFilter = (u: WP_DirUser) =>
    ModelsService.userDirectoryFilter(u, state.user?.id || 0);

  const fetch: WP_FetchPage<WP_DirUser> = (pagination) => {
    const result = wp.getUsers(pagination);
    return result.then((r) => {
      r.items = r.items.filter(userFilter);
      return r;
    });
  };

  const {
    items,
    setItems,
    fetchMore,
    hasMoreItems,
    loading,
    reset,
    isReset,
    page,
    totalPage,
  } = usePagination(fetch);

  const refreshUser: RefreshDirUserFunc = (userId: number): Promise<void> => {
    logger.info(`refreshUser() refresh user ${userId}...`);
    return new Promise<void>((resolve, reject) => {
      wp.getUser(userId).then((u: WP_DirUser) => {
        const copy = JSON.parse(JSON.stringify(items));
        const copyItemIdx = copy.findIndex((u: WP_DirUser) => u.id === userId);
        if (copyItemIdx >= 0) {
          copy[copyItemIdx] = u;
        }
        setItems(copy);
      });
    });
  };

  return {
    reset: reset,
    isReset: isReset,
    fetchUsers: () => {
      return fetchMore(per_page, {});
    },
    hasMoreUsers: hasMoreItems,
    isLoading: loading,
    page: page,
    totalPage: totalPage,
    users: items,
    refreshUser: refreshUser,
  };
};
